import React from 'react';
import type { BetStrategy } from '../../types';

interface StrategyCardProps {
  strategy: BetStrategy;
  onEdit: (strategy: BetStrategy) => void;
  onDelete: (strategyId: number) => void;
  onRun: (strategy: BetStrategy) => void;
  isRunning: boolean;
}

const BET_TYPE_LABELS: Record<string, string> = {
  btts: 'BTTS',
  btts_winner_home: 'Home + BTTS',
  btts_winner_away: 'Away + BTTS',
  btts_o2_5: 'BTTS + O2.5',
  o2_5: 'Over 2.5',
  winner_o2_5_home: 'Home + O2.5',
  winner_o2_5_away: 'Away + O2.5',
  cards_total_ou: 'Cards O/U',
  cards_team_home: 'Home Cards',
  cards_team_away: 'Away Cards',
  cards_first_timing: '1st Card <15',
  cards_red: 'Red Card',
};

const CONFIDENCE_LABELS: Record<string, string> = {
  high: 'High',
  moderate: 'Moderate',
  low: 'Low',
  speculative: 'Speculative',
};

export function StrategyCard({
  strategy,
  onEdit,
  onDelete,
  onRun,
  isRunning,
}: StrategyCardProps) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatProb = (prob: number | null) => {
    if (prob === null) return '-';
    return `${(prob * 100).toFixed(0)}%`;
  };

  return (
    <div className="strategy-card">
      <div className="strategy-card-header">
        <div className="strategy-card-title">
          <h3>{strategy.name}</h3>
          {strategy.alerts_enabled && (
            <span className="strategy-alerts-badge" title="Alerts enabled">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
              </svg>
            </span>
          )}
        </div>
        <div className="strategy-card-actions">
          <button
            className="strategy-action-btn strategy-action-run"
            onClick={() => onRun(strategy)}
            disabled={isRunning}
            title="Run strategy"
          >
            {isRunning ? (
              <span className="loading-spinner-small" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          <button
            className="strategy-action-btn strategy-action-edit"
            onClick={() => onEdit(strategy)}
            title="Edit strategy"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button
            className="strategy-action-btn strategy-action-delete"
            onClick={() => onDelete(strategy.id)}
            title="Delete strategy"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </div>

      {strategy.description && (
        <p className="strategy-card-description">{strategy.description}</p>
      )}

      <div className="strategy-card-details">
        {/* Bet Types */}
        <div className="strategy-detail-row">
          <span className="strategy-detail-label">Markets:</span>
          <div className="strategy-bet-types">
            {strategy.bet_types.map(type => (
              <span key={type} className="strategy-bet-type-chip">
                {BET_TYPE_LABELS[type] || type}
              </span>
            ))}
          </div>
        </div>

        {/* Time Range */}
        <div className="strategy-detail-row">
          <span className="strategy-detail-label">Time Range:</span>
          <span className="strategy-detail-value">Next {strategy.days_ahead} day{strategy.days_ahead > 1 ? 's' : ''}</span>
        </div>

        {/* Probability Filter */}
        {(strategy.min_probability !== null || strategy.max_probability !== null) && (
          <div className="strategy-detail-row">
            <span className="strategy-detail-label">Probability:</span>
            <span className="strategy-detail-value">
              {strategy.min_probability !== null && strategy.max_probability !== null
                ? `${formatProb(strategy.min_probability)} - ${formatProb(strategy.max_probability)}`
                : strategy.min_probability !== null
                  ? `>= ${formatProb(strategy.min_probability)}`
                  : `<= ${formatProb(strategy.max_probability)}`
              }
            </span>
          </div>
        )}

        {/* Confidence Levels */}
        {strategy.confidence_levels.length > 0 && (
          <div className="strategy-detail-row">
            <span className="strategy-detail-label">Confidence:</span>
            <span className="strategy-detail-value">
              {strategy.confidence_levels.map(c => CONFIDENCE_LABELS[c] || c).join(', ')}
            </span>
          </div>
        )}

        {/* Cards Line */}
        {strategy.cards_line !== null && (
          <div className="strategy-detail-row">
            <span className="strategy-detail-label">Cards Line:</span>
            <span className="strategy-detail-value">
              {strategy.cards_direction === 'over' ? 'Over' : 'Under'} {strategy.cards_line}
            </span>
          </div>
        )}

        {/* Leagues */}
        {strategy.league_ids.length > 0 && (
          <div className="strategy-detail-row">
            <span className="strategy-detail-label">Leagues:</span>
            <span className="strategy-detail-value">{strategy.league_ids.length} selected</span>
          </div>
        )}
      </div>

      <div className="strategy-card-footer">
        <span className="strategy-last-run">
          Last run: {formatDate(strategy.last_run_at)}
        </span>
        <span className="strategy-created">
          Created: {formatDate(strategy.created_at)}
        </span>
      </div>
    </div>
  );
}

export default StrategyCard;
